import { observer, useLocalObservable } from 'mobx-react';
import { useStore } from '../../../store/store';
import { paths } from '../../../utils/constants/routes';
import { useEffect } from 'react';
import { Link } from 'react-router';
import {
  FUNDS_VIEW_MODE,
  PORTFOLIOS_VIEW_TABS,
  PORTFOLIOS_VIEW_ANALYTICS_TABS_EVENTS
} from '../constants';
import {
  PORTFOLIO_FIELDS,
  getErrorFields,
  compareFields,
  trimStateFields
} from '../../../utils/constants/fields';
import { runInAction } from 'mobx';
import Input from '../../../components/inputs/Input';
import TextArea from '../../../components/inputs/TextArea';
import MultiSelect from '../../../components/inputs/MultiSelect';
import RangeInput from '../../../components/inputs/RangeInput';
import { mapFieldsToState } from '../../../utils/utils';
import useHistory from '../../../hooks/useHistory';
import { eventPageviewPortfolioCoCreate, eventPortfolioCoCreationStep } from '../../../ga4/ga4';
import { mapData } from '../../../api/dataMappers';
import { GA_EVENTS } from '../../../ga4/constants';
import { HEADER_MESSAGE_IDS, HEADER_MESSAGE_TYPES } from '../../../utils/constants/header';

const PortfolioView = observer(({ mode = FUNDS_VIEW_MODE.CREATE, portfolio = null }) => {
  const { fundsStore, utilsStore } = useStore();
  const { goBack } = useHistory();
  const state = useLocalObservable(() => ({
    portfolioId: '',
    setPortfolioId: (id = '') => (state.portfolioId = id),
    fields: {
      ...mapFieldsToState(PORTFOLIO_FIELDS),
      [PORTFOLIO_FIELDS.NAME.NAME]:
        mode === FUNDS_VIEW_MODE.CREATE ? `Portfolio co. ${fundsStore.portfolios.length + 1}` : ''
    },
    setFieldValue: (field = {}, value) => {
      state.fields[field.NAME] = value;
    },
    prevTab: null,
    tab: PORTFOLIOS_VIEW_TABS.INFORMATION,
    setTab: (value = '', id = '') => {
      [state.tab, state.prevTab] = [value, state.tab];
      const tabDOM = document.getElementById(id);
      if (tabDOM) {
        setTimeout(() => {
          tabDOM.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        }, 0);
      }
    },
    onSubmitErrorState: false,
    setOnSubmitErrorState: (value = false) => (state.onSubmitErrorState = value),
    get validationFields() {
      return getErrorFields(Object.values(PORTFOLIO_FIELDS), state.fields, [
        PORTFOLIO_FIELDS.ACQUISITION_DATE
      ]);
    },
    get isRestoreDisabled() {
      const hasDiff =
        mode === FUNDS_VIEW_MODE.CREATE
          ? !!Object.entries(state.fields).find(([fieldName, fieldValue]) => {
              if (fieldName === PORTFOLIO_FIELDS.NAME.NAME) {
                return fieldValue !== `Portfolio co. ${fundsStore.portfolios.length + 1}`;
              }
              return Array.isArray(fieldValue) ? !!fieldValue.length : fieldValue !== '';
            })
          : compareFields(state.fields, portfolio);

      return (
        !hasDiff ||
        (mode === FUNDS_VIEW_MODE.CREATE && fundsStore.isCreatingPortfolio) ||
        (mode === FUNDS_VIEW_MODE.EDIT && fundsStore.portfoliosInAction[portfolio.id])
      );
    },
    get isSaveDisabled() {
      return (
        fundsStore.isLoadingCompanyFunds ||
        !fundsStore.companyFunds.length ||
        state.isRestoreDisabled ||
        state.validationFields.invalidFields.filter((f) => !f.isOnSubmit).length ||
        (state.onSubmitErrorState && state.validationFields.invalidFields.length)
      );
    },
    restore: () => {
      if (mode === FUNDS_VIEW_MODE.CREATE) {
        state.fields = {
          ...mapFieldsToState(PORTFOLIO_FIELDS),
          [PORTFOLIO_FIELDS.NAME.NAME]:
            mode === FUNDS_VIEW_MODE.CREATE
              ? `Portfolio co. ${fundsStore.portfolios.length + 1}`
              : ''
        };
      } else {
        const initial = mapFieldsToState(PORTFOLIO_FIELDS);
        Object.entries(initial).forEach(([fieldName, initialFieldValue]) => {
          state.fields[fieldName] = portfolio[fieldName] || initialFieldValue;
        });
      }
    },
    getAnalyticsFormData: () => {
      const data = mapData(state.fields, PORTFOLIO_FIELDS, true);
      return Object.fromEntries(
        Object.values(PORTFOLIO_FIELDS).map((fieldConfig) => {
          const fieldName = fieldConfig.NAME;
          const fieldValue = fieldConfig.VALUES_KEY
            ? utilsStore.getOptionName(data[fieldName], fieldConfig)
            : data[fieldName];
          return [fieldName, fieldValue];
        })
      );
    }
  }));

  useEffect(() => {
    if (!fundsStore.isLoadingCompanyFunds && !fundsStore.companyFunds.length) {
      utilsStore.setHeaderPinnedMessage(
        'At least one Fund should be created in your Company, before creating a Portfolio company profile.',
        HEADER_MESSAGE_TYPES.WARNING,
        HEADER_MESSAGE_IDS.PORTFOLIO_NEEDS_FUNDS
      );
    }
  }, [utilsStore, fundsStore.companyFunds, fundsStore.isLoadingCompanyFunds]);

  useEffect(() => {
    return () => {
      if (utilsStore.headerPinnedMessage.id === HEADER_MESSAGE_IDS.PORTFOLIO_NEEDS_FUNDS) {
        utilsStore.setHeaderPinnedMessage();
      }
    };
  }, [utilsStore]);

  useEffect(() => {
    window.scrollTo({ top: 0 });
    fundsStore.loadCompanyFunds();
  }, [fundsStore]);

  useEffect(() => {
    const tabTitle = {
      [FUNDS_VIEW_MODE.CREATE]: 'Create',
      [FUNDS_VIEW_MODE.EDIT]: 'Edit'
    }[mode];
    document.title = `transact.digital GmbH - Portfolio company (${tabTitle})`;
  }, [mode]);

  useEffect(() => {
    if (mode === FUNDS_VIEW_MODE.CREATE) {
      eventPageviewPortfolioCoCreate();
    }
  }, [mode]);

  useEffect(() => {
    if (mode !== FUNDS_VIEW_MODE.CREATE || !state.prevTab) {
      return;
    }

    eventPortfolioCoCreationStep(
      PORTFOLIOS_VIEW_ANALYTICS_TABS_EVENTS[state.prevTab],
      state.getAnalyticsFormData()
    );
  }, [mode, state, state.prevTab]);

  useEffect(() => {
    if (mode === FUNDS_VIEW_MODE.CREATE) {
      return;
    }
    if (portfolio.id === state.portfolioId) {
      return;
    }

    if (mode === FUNDS_VIEW_MODE.EDIT && portfolio) {
      const initial = mapFieldsToState(PORTFOLIO_FIELDS);
      runInAction(() => {
        Object.keys(state.fields).forEach((fieldName) => {
          state.fields[fieldName] = portfolio[fieldName] || initial[fieldName];
        });
      });
    }
    state.setPortfolioId(portfolio.id);
  }, [state, mode, portfolio]);

  const tabConfig = [
    {
      path: PORTFOLIOS_VIEW_TABS.INFORMATION,
      desktopLabel: 'Portfolio co. information',
      id: 'PV_INF_TAB'
    },
    {
      path: PORTFOLIOS_VIEW_TABS.GEO_INDUSTRIES,
      desktopLabel: 'Geo & Industries',
      id: 'PV_GEO_TAB'
    },
    { path: PORTFOLIOS_VIEW_TABS.FINANCIAL_METRICS, desktopLabel: 'Financials', id: 'PV_FIN_TAB' },
    { path: PORTFOLIOS_VIEW_TABS.TRANSACTION_TYPES, desktopLabel: 'Transaction', id: 'PV_TRA_TAB' },
    { path: PORTFOLIOS_VIEW_TABS.VALUATION, desktopLabel: 'Valuation', id: 'PV_VAL_TAB' }
  ];

  const goToFirstErroredTab = () => {
    setTimeout(() => {
      const firstErroredTabDOM = document.querySelector('div.tabs > div.tab.error-tab');
      const tab = tabConfig.find((t) => t.id === firstErroredTabDOM?.id);
      if (!tab || tab.path === state.tab) {
        return;
      }

      state.setTab(tab.path, tab.id);
    }, 10);
  };

  const createPortfolio = () => {
    eventPortfolioCoCreationStep(
      PORTFOLIOS_VIEW_ANALYTICS_TABS_EVENTS[state.tab],
      state.getAnalyticsFormData()
    );

    trimStateFields(state.fields);
    if (state.validationFields.invalidFields.length) {
      if (!state.onSubmitErrorState) {
        state.setOnSubmitErrorState(true);
      }
      goToFirstErroredTab();
      return;
    }

    fundsStore.createPortfolio(state.fields, () => {
      eventPortfolioCoCreationStep(
        GA_EVENTS.portfolio_co_create_complete,
        state.getAnalyticsFormData()
      );
      goBack(paths.PORTOFLIO_COMPANIES);
    });
  };

  const editPortfolio = () => {
    if (state.validationFields.invalidFields.length) {
      if (!state.onSubmitErrorState) {
        state.setOnSubmitErrorState(true);
      }
      goToFirstErroredTab();
      return;
    }

    fundsStore.editPortfolio(portfolio, state.fields, () => {
      goBack(paths.PORTOFLIO_COMPANIES);
    });
  };

  const onBackClick = (e) => {
    e?.preventDefault?.();
    goBack(paths.PORTOFLIO_COMPANIES);
  };

  const erroredFields = Object.fromEntries(
    state.validationFields.invalidFields.map(({ name }) => [name, true])
  );

  const tabsInfo = [];
  tabConfig.forEach(({ path }) => {
    const tabInfo = { hasErrorField: false };
    tabsInfo.push(tabInfo);

    if (!state.onSubmitErrorState) {
      return;
    }

    const fields = Object.values(PORTFOLIO_FIELDS).filter((f) => f.PORTFOLIO_VIEW_TAB === path);
    fields.forEach((fieldConfig) => {
      if (erroredFields[fieldConfig.NAME]) {
        tabInfo.hasErrorField = true;
      }
    });
  });

  const disableAllFields =
    fundsStore.isLoadingCompanyFunds ||
    !fundsStore.companyFunds.length ||
    (mode === FUNDS_VIEW_MODE.CREATE
      ? fundsStore.isCreatingPortfolio
      : fundsStore.portfoliosInAction[portfolio.id]);

  return (
    <>
      <div className="header-backlink">
        <div className="gray-title-panel">
          <div className="title-section">
            <h4 className={state.fields[PORTFOLIO_FIELDS.NAME.NAME] ? '' : 'empty'}>
              {state.fields[PORTFOLIO_FIELDS.NAME.NAME] || 'Portfolio co. name'}
            </h4>
          </div>
          <h6>Create and manage your portfolio company profiles</h6>
          <div className="actions">
            <button
              className="btn btn-transparent btn-short disable-fade-transparent"
              disabled={state.isRestoreDisabled}
              onClick={state.restore}>
              Restore
            </button>
            <button
              className="btn btn-primary btn-short disable-fade-primary"
              disabled={state.isSaveDisabled}
              onClick={mode === FUNDS_VIEW_MODE.CREATE ? createPortfolio : editPortfolio}>
              Save
            </button>
          </div>
        </div>
        <Link to={paths.PORTOFLIO_COMPANIES} className="backlink" onClick={onBackClick}>
          Back to my portfolio companies
        </Link>
      </div>
      <div className="tabs">
        {tabConfig.map(({ path, desktopLabel, mobileLabel = '', id }, i) => {
          const isActive = state.tab === path;
          const isErrored = tabsInfo[i].hasErrorField;
          const tabClasNames = ['tab'];

          if (isActive) {
            tabClasNames.push('active');
          }

          if (isErrored && !isActive) {
            tabClasNames.push('error-tab');
          }

          return (
            <div
              key={path}
              id={id}
              className={tabClasNames.join(' ')}
              onClick={() => state.setTab(path, id)}>
              <div className="desktop-text">{desktopLabel}</div>
              <div className="mobile-text">{mobileLabel || desktopLabel}</div>
            </div>
          );
        })}
      </div>
      {state.tab === PORTFOLIOS_VIEW_TABS.INFORMATION && (
        <div className="form-body form-row-layout">
          <div className="row">
            <Input
              field={PORTFOLIO_FIELDS.NAME}
              value={state.fields[PORTFOLIO_FIELDS.NAME.NAME]}
              setFieldValue={state.setFieldValue}
              disabled={disableAllFields}
              inputWrap={{ enable: true, className: 'col' }}
              showOnSubmitErrorState={true}
              messages={state.validationFields.messages}
            />
            <Input
              field={PORTFOLIO_FIELDS.ACQUISITION_DATE}
              value={state.fields[PORTFOLIO_FIELDS.ACQUISITION_DATE.NAME]}
              setFieldValue={state.setFieldValue}
              disabled={disableAllFields}
              inputWrap={{ enable: true, className: 'col' }}
              showOnSubmitErrorState={state.onSubmitErrorState}
              messages={state.validationFields.messages}
            />
          </div>
          <div className="row">
            <Input
              field={PORTFOLIO_FIELDS.REVENUE}
              value={state.fields[PORTFOLIO_FIELDS.REVENUE.NAME]}
              setFieldValue={state.setFieldValue}
              disabled={disableAllFields}
              inputWrap={{ enable: true, className: 'col' }}
            />
            <Input
              field={PORTFOLIO_FIELDS.EBITDA}
              value={state.fields[PORTFOLIO_FIELDS.EBITDA.NAME]}
              setFieldValue={state.setFieldValue}
              disabled={disableAllFields}
              inputWrap={{ enable: true, className: 'col' }}
            />
          </div>
          <div className="row">
            <TextArea
              field={PORTFOLIO_FIELDS.DESCRIPTION}
              value={state.fields[PORTFOLIO_FIELDS.DESCRIPTION.NAME]}
              setFieldValue={state.setFieldValue}
              disabled={disableAllFields}
              messages={state.validationFields.messages}
              showOnSubmitErrorState={true}
              inputWrap={{ enable: true, className: 'col' }}
            />
            <MultiSelect
              field={PORTFOLIO_FIELDS.AFFILIATED_FUND}
              value={state.fields[PORTFOLIO_FIELDS.AFFILIATED_FUND.NAME]}
              values={fundsStore.companyFunds.map((f) => ({ name: f.name, value: f.id }))}
              setFieldValue={state.setFieldValue}
              disabled={disableAllFields}
              inputWrap={{ enable: true, className: 'col' }}
            />
          </div>
        </div>
      )}
      {state.tab === PORTFOLIOS_VIEW_TABS.GEO_INDUSTRIES && (
        <div className="form-body form-row-layout">
          <div className="row">
            <MultiSelect
              field={PORTFOLIO_FIELDS.GEOGRAPHIES}
              value={state.fields[PORTFOLIO_FIELDS.GEOGRAPHIES.NAME]}
              setFieldValue={state.setFieldValue}
              disabled={disableAllFields}
              inputWrap={{ enable: true, className: 'col' }}
            />
            <MultiSelect
              field={PORTFOLIO_FIELDS.INDUSTRIES}
              value={state.fields[PORTFOLIO_FIELDS.INDUSTRIES.NAME]}
              setFieldValue={state.setFieldValue}
              disabled={disableAllFields}
              inputWrap={{ enable: true, className: 'col' }}
            />
          </div>
        </div>
      )}
      {state.tab === PORTFOLIOS_VIEW_TABS.FINANCIAL_METRICS && (
        <div className="form-body form-row-layout">
          <div className="row">
            <RangeInput
              fieldMin={PORTFOLIO_FIELDS.REVENUE_RANGE_MIN}
              fieldMax={PORTFOLIO_FIELDS.REVENUE_RANGE_MAX}
              valueMin={state.fields[PORTFOLIO_FIELDS.REVENUE_RANGE_MIN.NAME]}
              valueMax={state.fields[PORTFOLIO_FIELDS.REVENUE_RANGE_MAX.NAME]}
              disabled={disableAllFields}
              setFieldValue={state.setFieldValue}
              messages={state.validationFields.messages}
              showOnSubmitErrorState={state.onSubmitErrorState}
              wrapClass={'col'}
            />
          </div>
          <div className="row">
            <RangeInput
              fieldMin={PORTFOLIO_FIELDS.EBITDA_RANGE_MIN}
              fieldMax={PORTFOLIO_FIELDS.EBITDA_RANGE_MAX}
              valueMin={state.fields[PORTFOLIO_FIELDS.EBITDA_RANGE_MIN.NAME]}
              valueMax={state.fields[PORTFOLIO_FIELDS.EBITDA_RANGE_MAX.NAME]}
              disabled={disableAllFields}
              setFieldValue={state.setFieldValue}
              messages={state.validationFields.messages}
              showOnSubmitErrorState={state.onSubmitErrorState}
              wrapClass={'col'}
            />
            <Input
              field={PORTFOLIO_FIELDS.EBITDA_MARGIN}
              value={state.fields[PORTFOLIO_FIELDS.EBITDA_MARGIN.NAME]}
              setFieldValue={state.setFieldValue}
              disabled={disableAllFields}
              inputWrap={{ enable: true, className: 'col' }}
            />
          </div>
          <div className="row">
            <RangeInput
              fieldMin={PORTFOLIO_FIELDS.EBIT_RANGE_MIN}
              fieldMax={PORTFOLIO_FIELDS.EBIT_RANGE_MAX}
              valueMin={state.fields[PORTFOLIO_FIELDS.EBIT_RANGE_MIN.NAME]}
              valueMax={state.fields[PORTFOLIO_FIELDS.EBIT_RANGE_MAX.NAME]}
              disabled={disableAllFields}
              setFieldValue={state.setFieldValue}
              messages={state.validationFields.messages}
              showOnSubmitErrorState={state.onSubmitErrorState}
              wrapClass={'col'}
            />
            <Input
              field={PORTFOLIO_FIELDS.EBIT_MARGIN}
              value={state.fields[PORTFOLIO_FIELDS.EBIT_MARGIN.NAME]}
              setFieldValue={state.setFieldValue}
              disabled={disableAllFields}
              inputWrap={{ enable: true, className: 'col' }}
            />
          </div>
        </div>
      )}
      {state.tab === PORTFOLIOS_VIEW_TABS.TRANSACTION_TYPES && (
        <div className="form-body form-row-layout">
          <div className="row">
            <MultiSelect
              field={PORTFOLIO_FIELDS.SITUATION}
              value={state.fields[PORTFOLIO_FIELDS.SITUATION.NAME]}
              setFieldValue={state.setFieldValue}
              disabled={disableAllFields}
              inputWrap={{ enable: true, className: 'col' }}
            />
            <MultiSelect
              field={PORTFOLIO_FIELDS.EQUITY}
              value={state.fields[PORTFOLIO_FIELDS.EQUITY.NAME]}
              setFieldValue={state.setFieldValue}
              disabled={disableAllFields}
              inputWrap={{ enable: true, className: 'col' }}
            />
          </div>
        </div>
      )}
      {state.tab === PORTFOLIOS_VIEW_TABS.VALUATION && (
        <div className="form-body form-row-layout">
          <div className="row">
            <RangeInput
              fieldMin={PORTFOLIO_FIELDS.ENTERPRISE_RANGE_MIN}
              fieldMax={PORTFOLIO_FIELDS.ENTERPRISE_RANGE_MAX}
              valueMin={state.fields[PORTFOLIO_FIELDS.ENTERPRISE_RANGE_MIN.NAME]}
              valueMax={state.fields[PORTFOLIO_FIELDS.ENTERPRISE_RANGE_MAX.NAME]}
              disabled={disableAllFields}
              setFieldValue={state.setFieldValue}
              messages={state.validationFields.messages}
              showOnSubmitErrorState={state.onSubmitErrorState}
              wrapClass={'col'}
            />
            <RangeInput
              fieldMin={PORTFOLIO_FIELDS.EQUITY_RANGE_MIN}
              fieldMax={PORTFOLIO_FIELDS.EQUITY_RANGE_MAX}
              valueMin={state.fields[PORTFOLIO_FIELDS.EQUITY_RANGE_MIN.NAME]}
              valueMax={state.fields[PORTFOLIO_FIELDS.EQUITY_RANGE_MAX.NAME]}
              disabled={disableAllFields}
              setFieldValue={state.setFieldValue}
              messages={state.validationFields.messages}
              showOnSubmitErrorState={state.onSubmitErrorState}
              wrapClass={'col'}
            />
          </div>
        </div>
      )}
    </>
  );
});

export default PortfolioView;
